import { z } from "zod";

// Chat between people on the same mail domain. People are identified by their mailbox address, so you can message
// a colleague before they have ever signed in to Workspace.

export const Presence = z.enum(["online", "away", "offline"]);

export const ChatPerson = z.object({
  email: z.string(),
  name: z.string(),
  presence: Presence,
});

export const ChatFile = z.object({
  id: z.string(),
  name: z.string(),
  mime: z.string(),
  size: z.number().int(),
});

export const ChatMessage = z.object({
  id: z.number().int(),
  channelId: z.string(),
  author: z.string(),
  body: z.string(),
  createdAt: z.string(),
  editedAt: z.string().nullable(),
  deleted: z.boolean(),
  replyTo: z.number().int().nullable(),
  file: ChatFile.nullable(),
  /** Echo of the sender's temporary id, so the sender can swap its optimistic copy for the stored one. */
  clientId: z.string().nullable().optional(),
});

export const ChatChannel = z.object({
  id: z.string(),
  kind: z.enum(["dm", "group"]),
  /** Group name; for a direct message the other person's name. */
  name: z.string(),
  topic: z.string(),
  members: z.array(z.string()),
  unread: z.number().int(),
  muted: z.boolean(),
  lastMessage: ChatMessage.nullable(),
  /** Highest message id each member has read, for "Seen" in direct messages. */
  readUpTo: z.record(z.string(), z.number().int()),
});

export const SendChatMessage = z.object({
  body: z.string().max(10_000),
  replyTo: z.number().int().positive().optional(),
  fileId: z.string().uuid().optional(),
  clientId: z.string().max(64).optional(),
});

export const CreateGroup = z.object({
  name: z.string().trim().min(1).max(80),
  members: z.array(z.string().email()).min(1).max(200),
});

export const UpdateChannel = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  topic: z.string().max(250).optional(),
  muted: z.boolean().optional(),
  addMembers: z.array(z.string().email()).max(200).optional(),
});

/** Largest file a chat message can carry. */
export const CHAT_FILE_MAX_BYTES = 10 * 1024 * 1024;

export type Presence = z.infer<typeof Presence>;
export type ChatPerson = z.infer<typeof ChatPerson>;
export type ChatFile = z.infer<typeof ChatFile>;
export type ChatMessage = z.infer<typeof ChatMessage>;
export type ChatChannel = z.infer<typeof ChatChannel>;
export type SendChatMessage = z.infer<typeof SendChatMessage>;
export type CreateGroup = z.infer<typeof CreateGroup>;
export type UpdateChannel = z.infer<typeof UpdateChannel>;
